
import React, { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { loadTaxonomy } from '@/lib/taxonomy-loader';
import { Checkbox } from '@/components/ui/checkbox';

interface FilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (filters: any) => void;
    activeFilters: any;
}

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose, onApply, activeFilters }) => {
    const [filters, setFilters] = useState(activeFilters);
    const [taxonomy, setTaxonomy] = useState<{ [key: string]: { type: string; options: string[] } }>({});

    // Load dynamic taxonomy
    useEffect(() => {
        const initializeTaxonomy = async () => {
            const data = await loadTaxonomy();
            if (data.categories) {
                setTaxonomy(data.categories);
            }
        };
        initializeTaxonomy();
    }, []);

    useEffect(() => {
        if (isOpen) {
            setFilters(activeFilters);
        }
    }, [isOpen, activeFilters]);

    const handleCheckboxChange = (category: string, option: string) => {
        setFilters((prev: any) => {
            const newCategoryFilters = new Set(prev[category] || []);
            if (newCategoryFilters.has(option)) {
                newCategoryFilters.delete(option);
            } else {
                newCategoryFilters.add(option);
            }
            if (newCategoryFilters.size === 0) {
                const newFilters = { ...prev };
                delete newFilters[category];
                return newFilters;
            }
            return { ...prev, [category]: newCategoryFilters };
        });
    };

    const handleApply = () => {
        onApply(filters);
        onClose();
    };
    
    const handleClear = () => {
        setFilters({});
        onApply({});
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Filter Drills" maxWidth="max-w-4xl">
            <div className="p-6 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Object.entries(taxonomy)
                        .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                        .map(([category, config]) => {
                        if (config.type === 'text') return null;
                        const sortedOptions = [...config.options].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                        return (
                            <div key={category}>
                                <h4 className="font-medium mb-2">{category}</h4>
                                <div className="flex flex-col gap-1">
                                    {sortedOptions.map(opt => (
                                        <label key={opt} className="flex items-center text-sm cursor-pointer gap-2">
                                            <Checkbox checked={filters[category]?.has(opt) || false} onCheckedChange={() => handleCheckboxChange(category, opt)} />
                                            {opt}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="p-4 border-t border-line flex justify-end gap-4">
                <button onClick={handleClear} className="bg-bg hover:bg-bg/80 text-fg font-bold py-2 px-6 rounded-lg border border-line">Clear All</button>
                <button onClick={handleApply} className="bg-accent text-accent-fg hover:bg-accent/90 font-bold py-2 px-6 rounded-lg">Apply Filters</button>
            </div>
        </Modal>
    );
};

export default FilterModal;
